import React, { Component, useCallback, useEffect, useRef } from 'react';
import noop from 'lodash/noop';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { useShallowStableReference } from '@atlassian/jira-utils-stable-reference/src/index.tsx';
import AppBase from '@atlassian/jira-app-base/src/index.tsx';
import ReportErrors from '@atlassian/jira-errors-handling/src/utils/reporting-error-boundary.tsx';
import {
	ContextualAnalyticsData,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { connect } from '@atlassian/jira-react-redux/src/index.tsx';
import { toIssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import type { IssueContext } from '../common/types.tsx';
import { setContext } from '../controllers/context/actions/index.tsx';
import {
	devPanelWillUnmount,
	fetchDevPanelRequest,
} from '../controllers/dev-summary/actions/index.tsx';
import {
	isLoadingDevPanelSelector,
	panelStateSelector,
} from '../controllers/dev-summary/selectors/index.tsx';
import type { State } from '../controllers/types.tsx';
import EmptySummary from './empty-summary/index.tsx';
import Summary from './summary/index.tsx';
import type { Props, StateProps, DispatchProps } from './types.tsx';
// eslint-disable-next-line jira/react/no-class-components
export class DevPanelAppOld extends Component<Props> {
	static defaultProps: Props = {
		issueAri: '',
		issueId: '',
		issueKey: toIssueKey(''),
		issueStatusCategory: '',
		isSiteAdmin: false,
		hasSoftwareAccess: false,
		projectId: '',
		projectType: '',
		nextGenProject: false,
		analyticsSource: '',
		panelState: 'HIDDEN',
		// Replace with lodash/noop
		// eslint-disable-next-line @typescript-eslint/no-empty-function
		onFetchDevPanel: () => {},
		// Replace with lodash/noop
		// eslint-disable-next-line @typescript-eslint/no-empty-function
		onWillUnMount: () => {},
		// Replace with lodash/noop
		// eslint-disable-next-line @typescript-eslint/no-empty-function
		onSetContext: () => {},
		projectAri: '',
		isJiraAdmin: false,
		isLoading: false,
	};

	constructor(props: Props) {
		super(props);
		this.dispatchSetContext();
	}

	UNSAFE_componentWillMount() {
		this.props.onFetchDevPanel();
	}

	componentDidUpdate(prevProps: Props) {
		if (this.props.isLoading !== prevProps.isLoading) {
			return;
		}

		if (this.props.panelState !== prevProps.panelState) {
			return;
		}

		this.dispatchSetContext();
		this.props.onFetchDevPanel();
	}

	componentWillUnmount() {
		this.props.onWillUnMount();
	}

	dispatchSetContext() {
		const {
			issueId,
			issueAri,
			issueKey,
			issueStatusCategory,
			projectId,
			projectType,
			nextGenProject,
			analyticsSource,
			isSiteAdmin,
			onSetContext,
			projectAri,
			isJiraAdmin,
		} = this.props;

		onSetContext({
			issueId,
			issueAri,
			issueKey,
			issueStatusCategory,
			projectId,
			projectType,
			nextGenProject,
			analyticsSource,
			isSiteAdmin: isSiteAdmin || false,
			projectAri,
			isJiraAdmin: isJiraAdmin || false,
		});
	}

	render() {
		return this.props.hasSoftwareAccess ? (
			<AppBase>
				<ContextualAnalyticsData
					attributes={{
						analyticsSource: this.props.analyticsSource,
						issueId: this.props.issueId,
						isEmptyStateEnabled: true,
						isSiteAdmin: this.props.isSiteAdmin,
					}}
				>
					<ReportErrors id="devPanelApp" packageName="jiraDevelopmentSummaryDevelopmentField">
						{this.props.panelState === 'DEV_SUMMARY' && <Summary />}
						{this.props.panelState === 'NOT_CONNECTED' && <EmptySummary />}
					</ReportErrors>
				</ContextualAnalyticsData>
			</AppBase>
		) : (
			<></>
		);
	}
}

export const DevPanelAppNew: React.FC<Props> = (unstableProps) => {
	const props = useShallowStableReference(unstableProps, fg('jfp_magma_issue-stability-phase1'));
	const {
		issueAri = '',
		issueId = '',
		issueKey = toIssueKey(''),
		issueStatusCategory = '',
		isSiteAdmin = false,
		hasSoftwareAccess = false,
		projectId = '',
		projectType = '',
		nextGenProject = false,
		analyticsSource = '',
		panelState = 'HIDDEN',
		onFetchDevPanel = noop,
		onWillUnMount = noop,
		onSetContext = noop,
		projectAri = '',
		isJiraAdmin = false,
		isLoading = false,
	} = props;

	const dispatchSetContext = useCallback(() => {
		onSetContext({
			issueId,
			issueAri,
			issueKey,
			issueStatusCategory,
			projectId,
			projectType,
			nextGenProject,
			analyticsSource,
			isSiteAdmin: isSiteAdmin || false,
			projectAri,
			isJiraAdmin: isJiraAdmin || false,
		});
	}, [
		analyticsSource,
		isJiraAdmin,
		isSiteAdmin,
		issueAri,
		issueId,
		issueKey,
		issueStatusCategory,
		nextGenProject,
		onSetContext,
		projectAri,
		projectId,
		projectType,
	]);

	const hasMounted = useRef(false);
	// UNSAFE_componentWillMount
	useEffect(() => {
		if (!issueId || hasMounted.current) {
			return;
		}
		dispatchSetContext();
		onFetchDevPanel();
		hasMounted.current = true;
		// componentWillUnmount
		return () => {
			onWillUnMount();
		};
	}, [dispatchSetContext, issueId, onFetchDevPanel, onWillUnMount]);

	const previousProps = useRef(props);

	useEffect(() => {
		const { isLoading: prevIsLoading, panelState: prevPanelState } = previousProps.current || {};
		previousProps.current = props;
		if (!hasMounted.current) {
			return;
		}
		if (isLoading !== prevIsLoading) {
			return;
		}

		if (panelState !== prevPanelState) {
			return;
		}
		dispatchSetContext();
		onFetchDevPanel();
	}, [dispatchSetContext, isLoading, onFetchDevPanel, panelState, props]);

	return hasSoftwareAccess ? (
		<AppBase>
			<ContextualAnalyticsData
				attributes={{
					analyticsSource,
					issueId,
					isEmptyStateEnabled: true,
					isSiteAdmin,
				}}
			>
				<ReportErrors id="devPanelApp" packageName="jiraDevelopmentSummaryDevelopmentField">
					{panelState === 'DEV_SUMMARY' && <Summary />}
					{panelState === 'NOT_CONNECTED' && <EmptySummary />}
				</ReportErrors>
			</ContextualAnalyticsData>
		</AppBase>
	) : (
		<></>
	);
};

export const DevPanelApp = componentWithCondition(
	() => fg('jira-concurrent-create-branch-double-call'),
	DevPanelAppNew,
	DevPanelAppOld,
);

const DevPanelWithTenantContext = ({ onFetchDevPanel, ...props }: Props) => {
	const { isSiteAdmin } = useTenantContext();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const wrappedOnFetchDevPanel = useCallback(
		() =>
			onFetchDevPanel(
				createAnalyticsEvent({
					actionSubject: 'panel',
				}),
			),
		[onFetchDevPanel, createAnalyticsEvent],
	);

	return (
		<>
			<UFOLoadHold hold={props.isLoading} name="issue-dev-panel-data-hold" />
			<DevPanelApp {...props} onFetchDevPanel={wrappedOnFetchDevPanel} isSiteAdmin={isSiteAdmin} />
		</>
	);
};

// This connect uses the store prop thats passed though not the provider that its underneath.

export default connect(
	(state: State): StateProps => ({
		panelState: panelStateSelector(state),
		isLoading: isLoadingDevPanelSelector(state),
	}),
	(dispatch): DispatchProps => ({
		onFetchDevPanel: (analyticsEvent?: UIAnalyticsEvent) => {
			dispatch(fetchDevPanelRequest(analyticsEvent));
		},
		onWillUnMount: () => dispatch(devPanelWillUnmount()),
		onSetContext: (issueContext: IssueContext) => dispatch(setContext(issueContext)),
	}),
)(DevPanelWithTenantContext);
